import { IkeUnitDistance, type UserJSONObject, type UserSettingsJSONObject } from '@ikegps/core-api';
import { IkeUnit } from '@ikegps/core-shared';
import { computed, makeObservable } from 'mobx';
import { T } from '../i18n';
import { CurrentState } from '../state/page.state';
import { type IkeClientService, Services } from './index';
import { UserMutator } from './user.service';

export const DATE_OPTIONS = ['MM/DD/YYYY', 'DD/MM/YYYY'];

export const DEFAULT_DATE = DATE_OPTIONS[0];
export const DEFAULT_UNIT: IkeUnitDistance = IkeUnit.FEET;

export const UNIT_OPTIONS = [
	{ unit: IkeUnitDistance.METER, label: T.UNIT_METER },
	{ unit: IkeUnitDistance.FEET, label: T.UNIT_FEET_INCH },
];

export class UserSettingsService implements IkeClientService {
	readonly __priority = 100;

	constructor() {
		makeObservable(this);
	}

	private getSettings(): UserSettingsJSONObject {
		var currentUser = Services.User.current;
		if (currentUser == null) {
			throw new Error('Invalid user.');
		}

		if (currentUser.settings == null) {
			currentUser.settings = {};
		}

		return currentUser.settings;
	}

	setDateFormat(dateFormat: string): Promise<UserJSONObject> {
		dateFormat = dateFormat.toUpperCase();
		if (DATE_OPTIONS.indexOf(dateFormat) === -1) {
			throw new Error('Invalid DateFormat.');
		}

		if (dateFormat === this.dateFormat) {
			return Promise.resolve(Services.User.current);
		}

		return UserMutator.setDateFormat(dateFormat);
	}

	setUnits(units: string): Promise<UserJSONObject> {
		const ikeUnits = IkeUnit.toUnitDistance(units);
		if (ikeUnits == null) {
			throw new Error('Invalid Unit: ' + units);
		}

		if (ikeUnits === this.units) {
			return Promise.resolve(Services.User.current);
		}

		return UserMutator.setUnits(ikeUnits);
	}

	setDepartment(departmentId: string): Promise<UserJSONObject> {
		if (departmentId == null) {
			throw new Error('Invalid departmentId');
		}

		if (departmentId === this.departmentId) {
			return Promise.resolve(Services.User.current);
		}

		// Discard any pending span link target changes before switching departments
		CurrentState.CollectionLink.discardAllModifiedTargets();
		CurrentState.CollectionLink.cancelLinking();

		Services.Route.departmentReset();

		return UserMutator.setDepartment(departmentId);
	}

	@computed get units(): IkeUnitDistance {
		if (Services.User.current == null) {
			return DEFAULT_UNIT;
		}

		const settings = this.getSettings();
		if (settings.units == null) {
			return DEFAULT_UNIT;
		}

		return settings.units || DEFAULT_UNIT;
	}

	@computed get dateFormat(): string {
		if (Services.User.current == null) {
			return DEFAULT_DATE;
		}

		const settings = this.getSettings();
		if (settings.date == null) {
			return DEFAULT_DATE;
		}

		return settings.date || DEFAULT_DATE;
	}

	@computed get departmentId(): string | null {
		if (Services.User.current == null) {
			return null;
		}

		const settings = this.getSettings();
		if (settings.departmentId != null) {
			return settings.departmentId;
		}

		return null;
	}
}
